import { Plus, Trash2, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

export const NITAQAT = ["بلاتيني", "أخضر مرتفع", "أخضر متوسط", "أخضر منخفض", "أحمر"];

const NUMS = [
  ["motorcycle_licenses", "ترخيص نهائي دراجات نارية (عدد)"], ["light_transport_licenses", "ترخيص النقل الخفيف نهائي (عدد)"],
  ["operating_cards", "عدد كروت التشغيل"], ["sponsored_workers", "عدد العمالة على الكفالة"],
  ["licensed_drivers", "عدد الحاصلين على رخص القيادة حالياً"], ["motorcycles_count", "عدد الدراجات النارية"],
  ["company_age", "عمر الشركة (بالسنوات)"], ["saudi_count", "عدد السعوديين على المنشأة"],
];
const TEXTS = [["headquarters", "المقر"], ["agreed_target", "التارجت المتفق عليه"]];
const LONGS = [["admin_structure", "الهيكل الإداري"], ["iqama_remaining", "المتبقي في إقامات العمال بالتفصيل (التجديد كل 3 أشهر)"]];
const BOOLS = [["has_housing", "يوجد سكن"], ["has_admin_office", "يوجد مكتب إداري"], ["has_municipal_license", "يوجد رخصة بلدي"]];
const TOGGLED = [
  ["has_delivery_contracts", "يوجد عقود مع تطبيقات التوصيل", "delivery_contracts_count", "عدد العقود", "num"],
  ["has_visas", "توجد تأشيرات متاحة", "visas_count", "عدد التأشيرات", "num"],
  ["has_financial_obligations", "عليها التزامات مالية", "financial_obligations", "اذكر الالتزامات بالتفصيل", "long"],
];

export const EMPTY_COMPANY = {
  ...Object.fromEntries(NUMS.map(([k]) => [k, ""])), headquarters: "", agreed_target: "", admin_structure: "", iqama_remaining: "",
  nationalities: [{ label: "", count: "" }], vehicles: [{ label: "", count: "" }], nitaqat: "",
  has_delivery_contracts: false, delivery_contracts_count: "", has_housing: false, has_admin_office: false, has_municipal_license: false,
  has_financial_obligations: false, financial_obligations: "", has_visas: false, visas_count: "",
};

const n = (v) => (v === "" || v == null ? null : Number(v));
export const cleanCompany = (c) => ({
  ...c,
  ...Object.fromEntries([...NUMS.map(([k]) => k), "delivery_contracts_count", "visas_count"].map((k) => [k, n(c[k])])),
  nationalities: c.nationalities.filter((r) => r.label.trim()).map((r) => ({ label: r.label.trim(), count: Number(r.count) || 0 })),
  vehicles: c.vehicles.filter((r) => r.label.trim()).map((r) => ({ label: r.label.trim(), count: Number(r.count) || 0 })),
});

const Rows = ({ title, rows, onChange, ph, testId }) => (
  <div className="sm:col-span-2 space-y-2" data-testid={testId}>
    <div className="text-xs font-semibold">{title}</div>
    {rows.map((r, i) => (
      <div key={i} className="flex gap-2">
        <Input data-testid={`${testId}-label-${i}`} value={r.label} placeholder={ph} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className="h-10 rounded-xl" />
        <Input data-testid={`${testId}-count-${i}`} type="number" min={0} value={r.count} placeholder="العدد" onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, count: e.target.value } : x)))} className="h-10 rounded-xl w-28" />
        <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
      </div>
    ))}
    <button type="button" data-testid={`${testId}-add`} onClick={() => onChange([...rows, { label: "", count: "" }])} className="text-xs font-bold text-brand flex items-center gap-1"><Plus size={14} />إضافة</button>
  </div>
);

export const CompanyDetailsForm = ({ c, set }) => {
  const up = (k, v) => set({ ...c, [k]: v });
  return (
    <div className="border-2 border-brand/20 rounded-xl p-5 space-y-4" data-testid="company-details-form">
      <h3 className="font-bold flex items-center gap-2"><Building2 size={18} className="text-brand" />تفاصيل المنشأة</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        {NUMS.map(([k, l]) => <div key={k}><label className="text-xs font-semibold">{l}</label><Input data-testid={`cd-${k}`} type="number" min={0} value={c[k]} onChange={(e) => up(k, e.target.value)} className="h-10 rounded-xl mt-1" /></div>)}
        {TEXTS.map(([k, l]) => <div key={k}><label className="text-xs font-semibold">{l}</label><Input data-testid={`cd-${k}`} value={c[k]} onChange={(e) => up(k, e.target.value)} className="h-10 rounded-xl mt-1" /></div>)}
        <div><label className="text-xs font-semibold">نطاق المنشأة</label>
          <select data-testid="cd-nitaqat" value={c.nitaqat} onChange={(e) => up("nitaqat", e.target.value)} className="w-full h-10 rounded-xl border px-3 bg-white text-sm mt-1"><option value="">اختر</option>{NITAQAT.map((x) => <option key={x}>{x}</option>)}</select>
        </div>
        <Rows title="جنسيات العمالة بالتفصيل وبالأعداد" ph="الجنسية" rows={c.nationalities} onChange={(v) => up("nationalities", v)} testId="cd-nationalities" />
        <Rows title="عدد السيارات وأنواعها" ph="نوع السيارة" rows={c.vehicles} onChange={(v) => up("vehicles", v)} testId="cd-vehicles" />
        {LONGS.map(([k, l]) => <div key={k} className="sm:col-span-2"><label className="text-xs font-semibold">{l}</label><Textarea data-testid={`cd-${k}`} value={c[k]} onChange={(e) => up(k, e.target.value)} className="rounded-xl mt-1" /></div>)}
        {BOOLS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm font-semibold"><Switch data-testid={`cd-${k}`} checked={c[k]} onCheckedChange={(v) => up(k, v)} />{l}</label>)}
        {TOGGLED.map(([b, l, k, kl, kind]) => (
          <div key={b} className="sm:col-span-2 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-semibold"><Switch data-testid={`cd-${b}`} checked={c[b]} onCheckedChange={(v) => up(b, v)} />{l}</label>
            {c[b] && (kind === "num"
              ? <Input data-testid={`cd-${k}`} type="number" min={0} placeholder={kl} value={c[k]} onChange={(e) => up(k, e.target.value)} className="h-10 rounded-xl w-40" />
              : <Textarea data-testid={`cd-${k}`} placeholder={kl} value={c[k]} onChange={(e) => up(k, e.target.value)} className="rounded-xl w-full" />)}
          </div>
        ))}
      </div>
    </div>
  );
};

const Item = ({ label, value }) => (
  <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">{label}</div><div className="font-bold mt-0.5 whitespace-pre-line">{value}</div></div>
);
const yn = (v) => (v ? "نعم" : "لا");
const has = (v) => v !== null && v !== undefined && v !== "";

export const CompanyDetailsView = ({ c }) => (
  <div className="bg-white border rounded-xl p-6" data-testid="company-details-view">
    <h2 className="font-bold mb-4 flex items-center gap-2"><Building2 size={18} className="text-brand" />تفاصيل المنشأة</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
      {[...NUMS, ...TEXTS, ["nitaqat", "نطاق المنشأة"]].filter(([k]) => has(c[k])).map(([k, l]) => <Item key={k} label={l.replace(" (عدد)", "")} value={c[k]} />)}
      {BOOLS.map(([k, l]) => <Item key={k} label={l.replace("يوجد ", "هل يوجد ")} value={yn(c[k])} />)}
      <Item label="عقود مع تطبيقات التوصيل" value={c.has_delivery_contracts ? `نعم${has(c.delivery_contracts_count) ? ` (${c.delivery_contracts_count})` : ""}` : "لا"} />
      <Item label="تأشيرات متاحة" value={c.has_visas ? `نعم${has(c.visas_count) ? ` (${c.visas_count})` : ""}` : "لا"} />
      <Item label="التزامات مالية" value={c.has_financial_obligations ? (c.financial_obligations || "نعم") : "لا"} />
      {c.nationalities?.length > 0 && <Item label="جنسيات العمالة" value={c.nationalities.map((r) => `${r.label}: ${r.count}`).join("\n")} />}
      {c.vehicles?.length > 0 && <Item label="السيارات وأنواعها" value={c.vehicles.map((r) => `${r.label}: ${r.count}`).join("\n")} />}
      {LONGS.filter(([k]) => c[k]).map(([k, l]) => <div key={k} className="sm:col-span-2 lg:col-span-3"><Item label={l} value={c[k]} /></div>)}
    </div>
  </div>
);
